import {
  ColDef,
  ValueFormatterParams,
  IServerSideGetRowsParams,
  GridReadyEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { dateFilterParams } from "../../../../utils/Formatter/AgGridFilter";
import { formatDate } from "../../../../utils/Date";
import { GraphQLClient } from "graphql-request";
import { createGraphQLClientWithMiddleware } from "../../../../services/graphqlClient";
import AgGrid from "../../../UI/AgGrid";
import { CURRENT_STOCK_BY_LOT_REPORT_VIEW } from "../../../../services/AgGrid/Report/InventoryReportAggrid";

interface Props {
  gridRef: RefObject<AgGridReact>;
}

const CurrentStockByLotReportTable = ({ gridRef }: Props) => {
  const { t } = useTranslation();

  const columnDefs: ColDef[] = [
    {
      field: "unique_id",
      headerName: t("inventory.items.unique_id"),
      filter: "agTextColumnFilter",
    },
    {
      field: "sku_name",
      headerName: t("inventory.items.sku"),
      filter: "agTextColumnFilter",
    },
    {
      field: "item_name",
      headerName: t("inventory.items.name"),
      filter: "agTextColumnFilter",
    },
    {
      field: "branch_id",
      headerName: t("branch.unique_id"),
      filter: "agTextColumnFilter",
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      field: "branch_unique_id",
      headerName: t("branch.unique_id"),
      filter: "agTextColumnFilter",
    },
    {
      field: "branch_name",
      headerName: t("branch.index"),
      filter: "agTextColumnFilter",
    },
    {
      field: "warehouse_id",
      headerName: t("inventory.warehouseId"),
      filter: "agTextColumnFilter",
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      field: "warehouse_unique_id",
      headerName: t("inventory.warehouseId"),
      filter: "agTextColumnFilter",
      hide: true,
    },
    {
      field: "warehouse_name",
      headerName: t("inventory.warehouse"),
      filter: "agTextColumnFilter",
    },
    {
      field: "bin_location_id",
      headerName: t("inventory.locationId"),
      filter: "agNumberColumnFilter",
      hide: true,
    },
    {
      field: "bin_location_name",
      headerName: t("inventory.location"),
      filter: "agTextColumnFilter",
    },
    {
      field: "serial_no",
      headerName: "Batch/SN",
      filter: "agTextColumnFilter",
    },
    {
      field: "barcode",
      headerName: "Barcode",
      filter: "agTextColumnFilter",
    },
    {
      field: "available_qty",
      headerName: t("inventory.quantities.available_qty"),
      filter: "agNumberColumnFilter",
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString() : 0,
      cellStyle: { display: "flex", justifyContent: "flex-end" },
    },
    {
      field: "stock_qty",
      headerName: t("inventory.quantities.stock_qty"),
      filter: "agNumberColumnFilter",
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString() : 0,
      cellStyle: { display: "flex", justifyContent: "flex-end" },
    },
    {
      field: "purchase_ordered_qty",
      headerName: t("inventory.quantities.purchase_ordered_qty"),
      filter: "agNumberColumnFilter",
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString() : 0,
      cellStyle: { display: "flex", justifyContent: "flex-end" },
    },
    {
      field: "transit_qty",
      headerName: t("inventory.quantities.in_transit_qty"),
      filter: "agNumberColumnFilter",
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString() : 0,
      cellStyle: { display: "flex", justifyContent: "flex-end" },
    },
    {
      field: "sale_committed_qty",
      headerName: t("inventory.quantities.sale_committed_qty"),
      filter: "agNumberColumnFilter",
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString() : 0,
      cellStyle: { display: "flex", justifyContent: "flex-end" },
    },
    {
      field: "lot_date",
      headerName: t("reports.lot_date"),
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value),
    },
    {
      field: "manufactured_date",
      headerName: t("date.manufactured_date"),
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value),
      hide: true,
    },
    {
      field: "expired_date",
      headerName: t("date.expired_date"),
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value),
      hide: true,
    },
  ];

  const graphQLClient: GraphQLClient = createGraphQLClientWithMiddleware("wms");

  const datasource = {
    async getRows(params: IServerSideGetRowsParams) {
      const { request } = params;
      const { startRow, endRow, filterModel, sortModel } = request;

      try {
        const { CurrentStockByLotReportView } = await graphQLClient.request(
          CURRENT_STOCK_BY_LOT_REPORT_VIEW,
          {
            aggridInput: {
              startRow,
              endRow,
              filterModel,
              sortModel,
            },
          }
        );
        params.success({
          rowData: CurrentStockByLotReportView.results,
          rowCount: CurrentStockByLotReportView.count,
        });
      } catch (err) {
        params.fail();
      }
    },
  };

  const onGridReady = (params: GridReadyEvent) => {
    params.api.setServerSideDatasource(datasource);
  };

  return (
    <AgGrid
      height={665}
      ref={gridRef}
      columnDefs={columnDefs}
      onGridReady={onGridReady}
    />
  );
};

export default CurrentStockByLotReportTable;
